import { useMemo } from "react";
import {
  Alert,
  Badge,
  Button,
  Group,
  Loader,
  Paper,
  SimpleGrid,
  Stack,
  Text,
} from "@mantine/core";
import {
  IconAlertTriangle,
  IconMail,
  IconPrinter,
  IconRoute,
} from "@tabler/icons-react";
import { CommunicationRedirectSettings } from "../../../services/admin/communicationSettings.service";

interface CommunicationSafetySummaryProps {
  settings?: CommunicationRedirectSettings;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
}

export default function CommunicationSafetySummary({
  settings,
  isLoading,
  isError,
  onRetry,
}: CommunicationSafetySummaryProps) {
  const hasFullyLiveChannel = useMemo(
    () =>
      Boolean(
        settings &&
          (!settings.emailRedirectEnabled || !settings.faxRedirectEnabled)
      ),
    [settings]
  );

  if (isLoading) {
    return (
      <Paper
        p="md"
        withBorder
        aria-label="Loading communication safety summary"
      >
        <Group gap="sm">
          <Loader size="sm" />
          <Text size="sm">Loading communication routing safeguards…</Text>
        </Group>
      </Paper>
    );
  }

  if (isError || !settings) {
    return (
      <Alert
        icon={<IconAlertTriangle size={18} />}
        color="red"
        variant="light"
        title="Routing safety summary unavailable"
        role="alert"
      >
        <Stack gap="sm">
          <Text size="sm">
            The current email and fax redirect state could not be confirmed.
          </Text>
          <Button variant="light" color="red" size="xs" onClick={onRetry}>
            Retry
          </Button>
        </Stack>
      </Alert>
    );
  }

  return (
    <Paper p="md" withBorder aria-label="Communication routing safety summary">
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Group gap="xs">
            <IconRoute size={20} />
            <Text fw={600}>Communication routing safeguards</Text>
          </Group>
          <Badge variant="light" color="violet">
            {settings.selectedPrefixes.length} bypass prefix
            {settings.selectedPrefixes.length === 1 ? "" : "es"}
          </Badge>
        </Group>

        {hasFullyLiveChannel && (
          <Alert
            icon={<IconAlertTriangle size={18} />}
            color="red"
            variant="light"
            title="One or more channels are fully live"
            role="alert"
          >
            Communications on a fully live channel are not protected by the
            redirect trap. Verify recipients before sending.
          </Alert>
        )}

        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
          <Paper p="sm" withBorder>
            <Group justify="space-between" wrap="nowrap">
              <Group gap="xs">
                <IconMail size={18} />
                <Text size="sm" fw={500}>
                  Email
                </Text>
              </Group>
              <Badge
                color={settings.emailRedirectEnabled ? "green" : "red"}
                variant="light"
              >
                {settings.emailRedirectEnabled
                  ? "Redirect safety ON"
                  : "FULLY LIVE"}
              </Badge>
            </Group>
          </Paper>
          <Paper p="sm" withBorder>
            <Group justify="space-between" wrap="nowrap">
              <Group gap="xs">
                <IconPrinter size={18} />
                <Text size="sm" fw={500}>
                  Fax
                </Text>
              </Group>
              <Badge
                color={settings.faxRedirectEnabled ? "green" : "red"}
                variant="light"
              >
                {settings.faxRedirectEnabled
                  ? "Redirect safety ON"
                  : "FULLY LIVE"}
              </Badge>
            </Group>
          </Paper>
        </SimpleGrid>
      </Stack>
    </Paper>
  );
}
